/**
 * Everything that belongs to the operator who was signed in, cleared in one place.
 *
 * Signing out set the auth state to null and stopped there. The `persist` middleware writes
 * those nulls back, so `admin-auth-storage` itself does end up empty — but everything the
 * operator accumulated ALONGSIDE it stayed on the machine for whoever signed in next. On a
 * shared workstation that is one operator inheriting another's session.
 *
 * The one that matters most is `impersonation_token`: it is written when an operator
 * impersonates a customer and, before this, was **never read and never removed anywhere in
 * either app** — a credential for someone else's account, parked in localStorage indefinitely.
 *
 * Mirrors `wi-cloud-client/src/stores/clearSessionData.ts`. Keep the two in step: a new
 * per-operator key added here without a line in this file is the same bug again. The one
 * exception is the React Query cache, cleared in `App.tsx` — importing the client here would
 * close an import cycle through `lib/api`.
 */

import { socketIOManager } from "@/lib/socketio";

/**
 * Keys wiped on sign-out, with the reason each one qualifies.
 *
 * Table FILTER state (`admin.*.filters`) is deliberately absent. Page size, sort and the date
 * window are how an operator has set up their workspace, not data about them — the same call
 * made on the client for `wicloud_resource_view_mode`. Clearing them would make every sign-out
 * feel like a fresh install for a privacy gain limited to a stale search box.
 */
const SESSION_KEYS = [
  // A live token for a CUSTOMER's account, minted when an operator impersonates them.
  "impersonation_token",
  // The operator's own saved price calculations, with their own UI to manage them.
  "priceCalculationHistory",
] as const;

export function clearSessionData(): void {
  for (const key of SESSION_KEYS) {
    localStorage.removeItem(key);
  }

  // The live-update sockets, still authenticated as this operator. Their only disconnect sat in an
  // effect of components the route guard had already unmounted, and `initialize()` reuses open
  // sockets, so the next operator on this tab inherited them. It never throws.
  void socketIOManager.disconnect();
}
